import { Merchant, User, Activation } from "src/models";
import { action, computed, observable } from "mobx";
import { createContext, useContext } from "react";
import { AsyncStore } from "src/stores";
import { IMerchantJSON, IActivationJSON } from "src/interfaces";
import { PassportService } from "src/services";
import { asyncRequest } from "src/decorators";
import { CookieConfig } from "src/helpers";

export type IPassportPaymentMethod = "card" | "basic";

export type IPassportBusinessType = "individual" | "corporate";

export class PassportStore extends AsyncStore<PassportStore> {
  @observable
  authUser = new User();

  @observable
  outlet = new Merchant();

  @observable
  activation = new Activation();

  @observable
  isAuthenticated = false;

  service = new PassportService();

  @observable
  selectedPaymentMethod: IPassportPaymentMethod = "basic";

  @observable
  selectedBusinessType: IPassportBusinessType = "individual";

  @observable
  fetchUrl!: string;

  @computed
  get isCorporate(): boolean {
    return this.selectedBusinessType === "corporate";
  }

  @computed
  get isCardPayment(): boolean {
    return this.selectedPaymentMethod === "card";
  }

  @computed
  get isImprovementEnabled(): boolean {
    return this.outlet.featureTypes.includes("MIDTRANS_PASSPORT_ACTIVATION");
  }

  @action
  setToken(token: string) {
    this.service.token = token;
  }

  @action
  @asyncRequest("getCurrentUser")
  async getCurrentUser() {
    const response = await this.service.getCurrentUser();
    this.authUser = User.fromJSON(response.data?.user);
    this.isAuthenticated = true;
    CookieConfig.userId = this.authUser.id;
  }

  @action
  applySelectionFromData() {
    this.selectedPaymentMethod = this.outlet.metadata
      ?.midtrans_credit_card_optin
      ? "card"
      : "basic";
    this.selectedBusinessType = this.outlet
      .businessType as IPassportBusinessType;
  }

  @action
  @asyncRequest("getUserOutlet")
  async getUserOutlet() {
    const outletId = this.authUser.merchantId;
    const response = await this.service.getUserOutlet(outletId);
    this.outlet = Merchant.fromJSON(response.data);
    this.applySelectionFromData();
  }

  @action
  @asyncRequest("updateOutlet")
  async updateOutlet(data: Partial<IMerchantJSON>, ignoreJSON = false) {
    const outletId = this.authUser.merchantId;
    const response = await this.service.updateOutlet(outletId, data);
    this.outlet = Merchant.fromJSON(response.data, ignoreJSON);
    this.applySelectionFromData();
  }

  @action
  @asyncRequest("getActivation")
  async getActivation() {
    const response = await this.service.getPassportActivation();
    this.activation = Activation.fromJSON(response.data.data);
  }

  @action
  @asyncRequest("updateActivation")
  async updateActivation(data: Partial<IActivationJSON>) {
    await this.service.updatePassportActivation(data);
    this.activation.updatePartial(data);
  }

  @asyncRequest("generateUrl")
  async generateUrl(userId: number, key: string) {
    const { data } = await this.service.generateUrl(userId, key);
    return data;
  }

  @action
  @asyncRequest("uploadFile")
  async uploadFile(userId: number, file: File) {
    const { upload_url, fetch_url } = await this.generateUrl(userId, file.name);
    await this.service.uploadFile(upload_url, file);
    this.fetchUrl = fetch_url;
    return fetch_url;
  }

  @action
  @asyncRequest("doPassportKYB")
  async doPassportKYB() {
    const data = {
      merchant_id: this.outlet.id,
      nik: this.outlet.idNumber,
      verify_basic: true,
      verify_selfie: true,
      name: this.outlet.idName,
    };

    await this.service.doPassportKYB(data);
    return;
  }

  @action
  @asyncRequest("recordConsent")
  async recordConsent(versionCode: string) {
    return await this.service.postConsent(versionCode);
  }
}

export const PassportStoreContext = createContext(new PassportStore());

export const usePassportStore = () => useContext(PassportStoreContext);
